import React, { useState, useEffect } from "react";
import axios from "axios";
import { useOutletContext } from "react-router-dom";

function EditJobRequestModal({ show, onClose, jobId }) {
  const { token, setGlobalLoading } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [jobData, setJobData] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [subdepartments, setSubdepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [workLocation, setWorkLocation] = useState("");
  const [workLocations, setWorkLocations] = useState([]);
  const [roleTypes] = useState(["Permanent", "Contract"]);
  const [allUsers, setAllUsers] = useState([]);
  const [hrAdmins, setHrAdmins] = useState([]);
  const [managementUsers, setManagementUsers] = useState([]);

  const [roleType, setRoleType] = useState("");
  const [requestingUser, setRequestingUser] = useState("");
  const [hrAdmin, setHrAdmin] = useState("");
  const [management, setManagement] = useState("");
  const [laptopRequired, setLaptopRequired] = useState(false);
  const [laptopSpecification, setLaptopSpecification] = useState("");

  const [filteredSubdepartments, setFilteredSubdepartments] = useState([]);
  const [filteredDesignations, setFilteredDesignations] = useState([]);

  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedSubdepartmentId, setSelectedSubdepartmentId] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchFilteredDesignations = async (subDeptId) => {
    try {
      setGlobalLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/employeeinfos/designations/${subDeptId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFilteredDesignations(res.data || []);
    } catch (err) {
      console.error("Failed to load filtered designations:", err);
      setFilteredDesignations([]);
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    const fetchJobData = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(
          `${API_BASE_URL}/employeeinfos/job/${jobId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = res.data;
        setJobData(data.job);
        setDepartments(data.departments || []);
        setSubdepartments(data.subdepartments || []);
        setDesignations(data.designations || []);
        setWorkLocation(data.job.worklocation || "");
        setWorkLocations(data.worklocations || []);
        setAllUsers(data.users || []);
        setHrAdmins(data.hradmins || []);
        setManagementUsers(data.mngt || []);
        setRoleType(data.job.role_type || "");
        setRequestingUser(data.job.createdBy?.toString() || "");
        setHrAdmin(data.job.assigned_to?.toString() || "");
        setManagement(data.job.approved_by?.toString() || "");
        setLaptopRequired(data.job.laptop === 1);
        setLaptopSpecification(data.job.lap_req || "");

        const deptId = data.job.department?.toString() || "";
        const subDeptId = data.job.sub_department?.toString() || "";
        const desigId = data.job.designation?.toString() || "";

        setSelectedDepartmentId(deptId);
        setSelectedSubdepartmentId(subDeptId);
        setSelectedDesignation(desigId);

        setFilteredSubdepartments(
          data.subdepartments.filter(
            (sub) => sub.parent_id.toString() === deptId
          )
        );

        if (subDeptId) {
          await fetchFilteredDesignations(subDeptId);
        }
      } catch (err) {
        console.error("Failed to fetch job data:", err);
        alert("Failed to load job details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobData();
  }, [show, jobId, token, API_BASE_URL]);

  useEffect(() => {
    if (!selectedDepartmentId) {
      setFilteredSubdepartments([]);
      setSelectedSubdepartmentId("");
      setSelectedDesignation("");
      setFilteredDesignations([]);
      return;
    }

    const filteredSubs = subdepartments.filter(
      (sub) => sub.parent_id.toString() === selectedDepartmentId
    );
    setFilteredSubdepartments(filteredSubs);

    if (
      !filteredSubs.some((sub) => sub.id.toString() === selectedSubdepartmentId)
    ) {
      setSelectedSubdepartmentId("");
      setSelectedDesignation("");
      setFilteredDesignations([]);
    }
  }, [selectedDepartmentId, subdepartments]);

  useEffect(() => {
    if (!selectedSubdepartmentId) {
      setFilteredDesignations([]);
      setSelectedDesignation("");
      return;
    }
    fetchFilteredDesignations(selectedSubdepartmentId);
  }, [selectedSubdepartmentId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Basic validation
      if (
        !selectedDepartmentId ||
        !selectedSubdepartmentId ||
        !selectedDesignation
      ) {
        alert(
          "Please fill all required fields: Department, Sub Department, and Designation."
        );
        setIsSaving(false);
        return;
      }
      const payload = {
        department: selectedDepartmentId,
        sub_department: selectedSubdepartmentId,
        designation: selectedDesignation,
        worklocation: workLocation,
        role_type: roleType,
        createdBy: requestingUser,
        assigned_to: hrAdmin,
        approved_by: management,
        laptop: laptopRequired ? 1 : 0,
        lap_req: laptopSpecification,
      };

      // Console log payload before sending
      console.log("Submitting payload:", payload);

      await axios.put(
        `${API_BASE_URL}/employeeinfos/job/${jobId}`,
        {
          department: selectedDepartmentId,
          sub_department: selectedSubdepartmentId,
          designation: selectedDesignation,
          worklocation: workLocation,
          role_type: roleType,
          createdBy: requestingUser,
          assigned_to: hrAdmin,
          approved_by: management,
          laptop: laptopRequired ? 1 : 0,
          lap_req: laptopSpecification,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Job request updated successfully.");
      onClose();
    } catch (err) {
      console.error("Failed to save job request", err);
      alert("Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "700px" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Job Request</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status" />
              </div>
            ) : jobData ? (
              <>
                <div className="mb-3">
                  <label className="form-label fw-bold">Department</label>
                  <select
                    className="form-select"
                    value={selectedDepartmentId}
                    onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  >
                    <option value="">-- Select Department --</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id.toString()}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Sub Department</label>
                  <select
                    className="form-select"
                    value={selectedSubdepartmentId}
                    onChange={(e) => setSelectedSubdepartmentId(e.target.value)}
                    disabled={!filteredSubdepartments.length}
                  >
                    <option value="">-- Select Sub Department --</option>
                    {filteredSubdepartments.map((sub) => (
                      <option key={sub.id} value={sub.id.toString()}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Designation</label>
                  <select
                    className="form-select"
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    disabled={!filteredDesignations.length}
                  >
                    <option value="">-- Select Designation --</option>
                    {filteredDesignations.map((des) => (
                      <option key={des.id} value={des.id.toString()}>
                        {des.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Work Location</label>
                  <select
                    className="form-select"
                    value={workLocation}
                    onChange={(e) => setWorkLocation(e.target.value)}
                  >
                    <option value="">-- Select Work Location --</option>
                    {workLocations.map((loc) => (
                      <option key={loc.id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Role Type</label>
                  <select
                    className="form-select"
                    value={roleType}
                    onChange={(e) => setRoleType(e.target.value)}
                  >
                    <option value="">-- Select Role Type --</option>
                    {roleTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Requesting User</label>
                  <select
                    className="form-select"
                    value={requestingUser}
                    onChange={(e) => setRequestingUser(e.target.value)}
                  >
                    <option value="">-- Select Requesting User --</option>
                    {allUsers.map((user) => (
                      <option key={user.id} value={user.id.toString()}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">HR Admin</label>
                  <select
                    className="form-select"
                    value={hrAdmin}
                    onChange={(e) => setHrAdmin(e.target.value)}
                  >
                    <option value="">-- Select HR Admin --</option>
                    {hrAdmins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Management</label>
                  <select
                    className="form-select"
                    value={management}
                    onChange={(e) => setManagement(e.target.value)}
                  >
                    <option value="">-- Select Management --</option>
                    {managementUsers.map((user) => (
                      <option key={user.id} value={user.id.toString()}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="laptopRequiredCheck"
                    checked={laptopRequired}
                    onChange={(e) => setLaptopRequired(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="laptopRequiredCheck"
                  >
                    Laptop Required
                  </label>
                </div>

                {laptopRequired && (
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Laptop Specification
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={laptopSpecification}
                      onChange={(e) => setLaptopSpecification(e.target.value)}
                    />
                  </div>
                )}
              </>
            ) : (
              <p>No job data found.</p>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditJobRequestModal;
